import React from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  Calendar, 
  User, 
  FileDigit, 
  Share2,
  Printer
} from 'lucide-react';
import billService from '../../services/billService';

const formatCurrency = (amount) => {
  if (isNaN(amount) || amount === null || amount === undefined) return '₹0';
  let cleanAmount = amount;
  const rounded = Math.round(amount);
  if (Math.abs(amount - rounded) < 0.02) {
    cleanAmount = rounded;
  }
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: cleanAmount % 1 === 0 ? 0 : 2,
  }).format(cleanAmount);
};

const formatDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear().toString().substring(2)}`;
};

const formatRate = (rate) => {
  if (Math.floor(rate) === rate) {
    return rate.toString();
  }
  return rate.toFixed(2).replace(/0*$/, '').replace(/\.$/, '');
};

const JaggedDivider = () => (
  <div style={styles.jaggedContainer}>
    <svg width="100%" height="10" viewBox="0 0 100 10" preserveAspectRatio="none">
      <path d="M0 10 L5 0 L10 10 L15 0 L20 10 L25 0 L30 10 L35 0 L40 10 L45 0 L50 10 L55 0 L60 10 L65 0 L70 10 L75 0 L80 10 L85 0 L90 10 L95 0 L100 10" 
            fill="none" stroke="#E2E8F0" strokeWidth="1" />
    </svg>
  </div>
);

const BillDetailsView = ({ bill, onBack, onViewPdf, onEdit, onDeleted }) => {
  const { t, lang } = useLang();
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  
  if (!bill) return null;

  const isGstBill = bill.items?.some(item => (item.taxAmount || 0) > 0);
  const getStatusInfo = () => {
    const type = bill.paymentType?.toLowerCase();
    if (type === 'cash' || type === 'online') return { text: t(LangKeys.bills_status_fully_paid), color: '#10B981' };
    if (type === 'unpaid') return { text: t(LangKeys.bills_status_unpaid), color: '#EF4444' };
    return { text: t(LangKeys.bill_details_status_pending || 'Pending'), color: '#F59E0B' };
  };
  
  const statusInfo = getStatusInfo();
  
  const itemSubtotal = bill.items?.reduce((sum, item) => {
    const displayPrice = item.originalPrice > 0 
      ? item.originalPrice 
      : (item.unitPrice > 0 ? item.unitPrice : (item.total / (item.quantity || 1)));
    return sum + (item.quantity * displayPrice);
  }, 0) || 0;
  const itemsDiscountSum = bill.items?.reduce((sum, item) => sum + ((item.discountValue || 0) * (item.quantity || 1)), 0) || 0;
  const totalDiscount = itemsDiscountSum;
  const billTotalDiscount = bill.totalDiscount || bill.total_discount || 0;
  const extraDiscount = billTotalDiscount - itemsDiscountSum > 0.01 ? billTotalDiscount - itemsDiscountSum : 0;
  const totalIgst = bill.items?.reduce((sum, item) => sum + (item.igst || 0), 0) || 0;
  const totalTax = bill.items?.reduce((sum, item) => sum + (item.taxAmount || 0), 0) || 0;
  const totalCgst = totalIgst > 0 ? 0 : Math.round((bill.items?.reduce((sum, item) => sum + (item.cgst || 0), 0) || 0) * 100) / 100;
  const totalSgst = totalIgst > 0 ? 0 : Math.round((bill.items?.reduce((sum, item) => sum + (item.sgst || 0), 0) || 0) * 100) / 100;

  const handleDelete = async () => {
    const success = await billService.deleteBill(bill.id);
    if (success) {
      setShowDeleteConfirm(false);
      if (onDeleted) onDeleted();
    } else {
      alert("Failed to delete bill");
    }
  };

  const getBillTitle = () => {
    let title = "";
    if (bill.type === 'sale') {
      title = isGstBill ? t(LangKeys.add_bill_sale_gst_bill) : t(LangKeys.bills_sale_bill);
    } else if (bill.type === 'purchase') {
      title = isGstBill ? t(LangKeys.add_bill_purchase_gst_bill) : t(LangKeys.bills_purchase_bill);
    } else {
      title = t(LangKeys.bills_expense_bill);
    }
    return `${title} #${bill.billNumber}`;
  };

  return (
    <div style={styles.detailContainer}>
      <div style={styles.detailHeader}>
         <div style={styles.headerLeft}>
            <button onClick={onBack} style={styles.backBtn}><ArrowLeft size={20} /></button>
            <h3 style={styles.detailTitle}>{getBillTitle()}</h3>
         </div>
         <div style={styles.headerActions}>
            <button style={styles.iconActionBtn} onClick={() => onEdit(bill)}><Edit size={18} color="#1D4ED8" /></button>
            <button style={styles.iconActionBtn} onClick={() => setShowDeleteConfirm(true)}><Trash2 size={18} color="#1D4ED8" /></button>
         </div>
      </div>

      <div style={styles.detailContent}>
         {/* DATE AND STATUS CARD */}
         <div style={styles.detailCard}>
            <div style={styles.detailCardRow}>
               <div style={styles.cardInfoGroup}>
                  <span style={styles.cardLabel}>{t(LangKeys.date)}</span>
                  <div style={styles.cardValueRow}>
                     <Calendar size={14} color="#1D4ED8" />
                     <span style={styles.cardValue}>{formatDate(bill.date)}</span>
                  </div>
               </div>
                <div style={{...styles.cardInfoGroup, alignItems: 'flex-end'}}>
                   <span style={styles.cardLabel}>{t(LangKeys.bill_details_status)}</span>
                   <span style={{...styles.cardValue, color: statusInfo.color}}>
                     {statusInfo.text}
                   </span>
                </div>
            </div>
         </div>

         {/* PARTY SECTION */}
         <div style={styles.detailCard}>
            <div style={styles.partyRow}>
               <div style={styles.avatarBox}><User size={20} color="#64748B" /></div>
               <div style={styles.partyInfo}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                     <span style={styles.partyName}>{bill.customer.customerName}</span>
                     {bill.customer.gstin && <span style={styles.gstBadge}>GST: {bill.customer.gstin}</span>}
                  </div>
                  {bill.customer.mobileNo && <span style={styles.partySub}>{bill.customer.mobileNo}</span>}
                  {bill.customer.address && <span style={styles.partyAddress}>{typeof bill.customer.address === 'string' ? bill.customer.address : (bill.customer.address.area || '')}</span>}
               </div>
            </div>
         </div>

         {/* PRIVATE NOTES */}
         {bill.privateNotes && (
           <div style={styles.detailCard}>
              <span style={styles.cardLabel}>{t(LangKeys.notes)}</span>
              <p style={styles.notesText}>{bill.privateNotes}</p>
           </div>
         )}

         {/* ITEMS SECTION */}
         <div style={styles.detailCard}>
            <span style={styles.cardLabel}>{(t(LangKeys.add_bill_n_items) || '{n} Items').replace('{n}', bill.items?.length || 0)}</span>
            <div style={styles.itemsList}>
                {bill.items?.map((item, idx) => {
                  const hasDiscount = item.totalDiscount > 0;
                  const hasGst = item.taxAmount > 0;

                  const pTaxableValue = (item.subtotal || 0) - (item.totalDiscount || 0);
                  const gstRate = pTaxableValue > 0 ? (item.taxAmount || 0) / pTaxableValue * 100 : 0;

                  const originalPricePerUnit = (item.taxIncluded || item.item?.taxIncluded)
                    ? item.originalPrice * (1 + gstRate / 100)
                    : item.originalPrice;

                  const strikethroughPrice = originalPricePerUnit * item.quantity;

                  return (
                    <div key={idx} style={styles.itemRowDetail}>
                       <div style={styles.itemMain}>
                          <span style={styles.itemNameDetail}>{item.name}</span>
                          <span style={styles.itemMetaDetail}>
                            {item.quantity.toFixed(1)} x {formatCurrency(originalPricePerUnit)}
                          </span>
                       </div>
                       <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                             {hasDiscount && (
                               <span style={{ color: '#94A3B8', fontSize: '13px', textDecoration: 'line-through' }}>
                                  {formatCurrency(strikethroughPrice)}
                               </span>
                             )}
                             <span style={styles.itemTotalDetail}>{formatCurrency(item.total)}</span>
                          </div>
                          {hasDiscount && (
                            <span style={{ color: '#10B981', fontSize: '11px', fontWeight: '500', marginTop: '2px' }}>
                               {item.discountType === 'percentage'
                                 ? `${formatRate(item.discountValue)}% = ${formatCurrency(item.totalDiscount)} discount`
                                 : `${formatCurrency(item.totalDiscount)} discount`}
                            </span>
                          )}
                          {hasGst && (
                            <span style={{ color: '#94A3B8', fontSize: '11px', marginTop: '2px' }}>
                               GST Rate: {formatRate(gstRate)}%
                            </span>
                          )}
                       </div>
                    </div>
                  );
                })}
            </div>
         </div>

         {/* SUMMARY SECTION */}
         <div style={styles.detailCard}>
            <div style={styles.summaryTable}>
               <div style={styles.summaryRow}><span style={styles.summaryLabel}>{t(LangKeys.add_bill_item_subtotal)}</span><span style={styles.summaryVal}>{formatCurrency(itemSubtotal)}</span></div>
               {totalDiscount > 0 && (
                   <div style={styles.summaryRow}><span style={styles.summaryLabel}>{t(LangKeys.add_bill_discount)}</span><span style={{...styles.summaryVal, color: '#EF4444'}}>- {formatCurrency(totalDiscount)}</span></div>
               )}
               {extraDiscount > 0 && (
                   <div style={styles.summaryRow}><span style={styles.summaryLabel}>{t(LangKeys.add_bill_extra_discount)}</span><span style={{...styles.summaryVal, color: '#EF4444'}}>- {formatCurrency(extraDiscount)}</span></div>
               )}
               
               {isGstBill && (
                 <>
                   {totalCgst > 0 && <div style={styles.summaryRow}><span style={styles.summaryLabel}>CGST</span><span style={styles.summaryVal}>{formatCurrency(totalCgst)}</span></div>}
                   {totalSgst > 0 && <div style={styles.summaryRow}><span style={styles.summaryLabel}>SGST</span><span style={styles.summaryVal}>{formatCurrency(totalSgst)}</span></div>}
                   {totalIgst > 0 && <div style={styles.summaryRow}><span style={styles.summaryLabel}>IGST</span><span style={styles.summaryVal}>{formatCurrency(totalIgst)}</span></div>}
                 </>
               )}
               
               {bill.roundOffAmount !== 0 && (
                 <div style={styles.summaryRow}><span style={styles.summaryLabel}>{t(LangKeys.add_bill_round_off)}</span><span style={styles.summaryVal}>{formatCurrency(bill.roundOffAmount)}</span></div>
               )}

               <div style={{margin: '12px 0'}}><JaggedDivider /></div>
               
               <div style={styles.summaryRow}>
                  <span style={{...styles.summaryLabel, fontSize: '16px', fontWeight: '800', color: '#0F172A'}}>{t(LangKeys.add_bill_bill_amount)}</span>
                  <span style={{...styles.summaryVal, fontSize: '18px', fontWeight: '800', color: '#0F172A'}}>{formatCurrency(bill.finalBillAmount)}</span>
               </div>
            </div>
         </div>
      </div>

      <div style={styles.detailBottomBar}>
         <button style={styles.pdfBtn} onClick={onViewPdf}>
            <FileDigit size={18} />
            <span>{t(LangKeys.bill_details_view_pdf).toUpperCase()}</span>
         </button>
         <button style={styles.shareBtn}>
            <Share2 size={18} />
            <span>{t(LangKeys.share).toUpperCase()}</span>
         </button>
      </div>

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteConfirm && (
        <div style={styles.modalOverlay}>
           <div style={styles.confirmBox}>
              <h4 style={styles.confirmTitle}>{t(LangKeys.bill_details_delete_confirmation_title)}</h4>
              <p style={styles.confirmText}>{t(LangKeys.bill_details_delete_confirmation_content)}</p>
              <div style={styles.confirmActions}>
                 <button style={styles.cancelBtn} onClick={() => setShowDeleteConfirm(false)}>{t(LangKeys.cancel).toUpperCase()}</button>
                 <button style={styles.deleteBtn} onClick={handleDelete}>{t(LangKeys.bill_details_delete_button).toUpperCase()}</button>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  detailContainer: { display: 'flex', flexDirection: 'column', height: '100%', background: '#F8FAFC' },
  detailHeader: { padding: '20px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'white' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  headerActions: { display: 'flex', gap: '8px' },
  backBtn: { width: '36px', height: '36px', borderRadius: '10px', border: 'none', background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  detailTitle: { fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 },
  iconActionBtn: { width: '36px', height: '36px', borderRadius: '10px', border: '1px solid #E2E8F0', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  detailContent: { flex: 1, padding: '20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' },
  detailCard: { background: 'white', borderRadius: '16px', padding: '16px', border: '1px solid #E2E8F0' },
  detailCardRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  cardInfoGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
  cardLabel: { fontSize: '12px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase' },
  cardValueRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  cardValue: { fontSize: '15px', fontWeight: '800', color: '#0F172A' },
  partyRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  avatarBox: { width: '40px', height: '40px', borderRadius: '10px', background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  partyInfo: { flex: 1, display: 'flex', flexDirection: 'column' },
  partyName: { fontSize: '16px', fontWeight: '800', color: '#0F172A' },
  partySub: { fontSize: '13px', color: '#64748B', fontWeight: '600' },
  itemsList: { marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' },
  itemRowDetail: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: '12px', borderBottom: '1px solid #F1F5F9' },
  itemMain: { display: 'flex', flexDirection: 'column', gap: '2px' },
  itemNameDetail: { fontSize: '14px', fontWeight: '700', color: '#0F172A' },
  itemMetaDetail: { fontSize: '12px', color: '#64748B', fontWeight: '600' },
  itemTotalDetail: { fontSize: '15px', fontWeight: '800', color: '#0F172A' },
  summaryTable: { display: 'flex', flexDirection: 'column', gap: '8px' },
  summaryRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  summaryLabel: { fontSize: '14px', fontWeight: '600', color: '#64748B' },
  summaryVal: { fontSize: '15px', fontWeight: '700', color: '#0F172A' },
  jaggedContainer: { width: '100%', height: '10px', margin: '4px 0' },
  detailBottomBar: { padding: '16px 20px', background: 'white', borderTop: '1px solid #E2E8F0', display: 'flex', gap: '12px' },
  pdfBtn: { flex: 1, height: '48px', borderRadius: '12px', border: '1px solid #1D4ED8', background: 'white', color: '#1D4ED8', fontWeight: '800', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' },
  shareBtn: { flex: 1, height: '48px', borderRadius: '12px', border: 'none', background: '#1D4ED8', color: 'white', fontWeight: '800', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' },
  modalOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  confirmBox: { background: 'white', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '340px', display: 'flex', flexDirection: 'column', gap: '16px' },
  confirmTitle: { fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 },
  confirmText: { fontSize: '14px', color: '#64748B', lineHeight: '1.5', margin: 0 },
  confirmActions: { display: 'flex', gap: '12px' },
  cancelBtn: { flex: 1, height: '40px', borderRadius: '10px', border: '1px solid #E2E8F0', background: 'white', color: '#64748B', fontWeight: '700', fontSize: '13px', cursor: 'pointer' },
  deleteBtn: { flex: 1, height: '40px', borderRadius: '10px', border: 'none', background: '#EF4444', color: 'white', fontWeight: '700', fontSize: '13px', cursor: 'pointer' },
  gstBadge: { fontSize: '11px', fontWeight: '700', color: '#1D4ED8', background: '#EFF6FF', padding: '2px 8px', borderRadius: '6px', textTransform: 'uppercase' },
  partyAddress: { fontSize: '12px', color: '#64748B', fontWeight: '500', marginTop: '2px' },
  notesText: { fontSize: '14px', color: '#334155', fontWeight: '500', margin: '8px 0 0 0', lineHeight: '1.5' },
};

export default BillDetailsView;
